import { GetAdminItineraryImportsResponse } from '@orbit/api-types'
import { useAdminFilter } from '@pages/Admin/AdminFilterContext'
import api from '@lib/api'
import { queryKeys } from '@lib/queryKeys'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

interface UseAdminItineraryImportsParams {
	page: number
	pageSize?: number
	/** Search by trip name or requester name/email; falsy values are omitted. */
	q?: string
	/** Narrow to one import status; omitted lists every status. */
	status?: GetAdminItineraryImportsResponse['imports'][number]['status']
}

/**
 * Fetches a page of the admin itinerary-imports list (GET /v1/admin/itinerary-imports),
 * scoped to the global creation-date range from {@link useAdminFilter} plus page,
 * search term and status. Previous data is kept while the next page loads. Gated
 * server-side by AdminGuard.
 */
export const useAdminItineraryImports = ({ page, pageSize, q, status }: UseAdminItineraryImportsParams) => {
	const { from, to, includeInternal } = useAdminFilter()

	return useQuery<GetAdminItineraryImportsResponse>({
		queryKey: queryKeys.adminItineraryImports(from, to, page, pageSize, q, status, includeInternal),
		queryFn: async () => {
			const response = await api.get<GetAdminItineraryImportsResponse>('/admin/itinerary-imports', {
				params: {
					from,
					to,
					page,
					pageSize,
					q: q || undefined,
					status,
					includeInternal: includeInternal || undefined,
				},
			})
			return response.data
		},
		placeholderData: keepPreviousData,
	})
}
